import { VStack, Text, Input, HStack, Box, RadioCard } from "@chakra-ui/react"
import { Field } from "@hydrologiq/shared/chakra/Field"
import { Tooltip } from "@hydrologiq/shared/chakra/Tooltip"
import { validateQuantity } from "../utils/quantityValidation"

export interface CollectionSectionProps {
  collectionReplacement: boolean
  quantityCollected: string
  onCollectionReplacementChange: (value: boolean) => void
  onQuantityCollectedChange: (value: string) => void
  quantityCollectedError: string | null
}

const COLLECTED_TOOLTIP_TEXT =
  "For the purpose of consumption forecasting, we adjust the remaining on-site quantity before refuelling. This helps to improve accuracy."

/**
 * Validates the collection quantity input.
 * Returns null if valid, or an error message string if invalid.
 */
export function validateCollectionInput(
  collectionReplacement: boolean,
  quantityCollected: string
): string | null {
  if (collectionReplacement) return null
  return validateQuantity(quantityCollected)
}

export function CollectionSection({
  collectionReplacement,
  quantityCollected,
  onCollectionReplacementChange,
  onQuantityCollectedChange,
  quantityCollectedError,
}: CollectionSectionProps) {
  return (
    <VStack gap={3} align="stretch" width="100%">
      <HStack gap={1}>
        <Text fontWeight="semibold" fontSize="sm">
          Collection upon delivery
        </Text>
        <Tooltip content={COLLECTED_TOOLTIP_TEXT}>
          <Box
            as="span"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            width="16px"
            height="16px"
            borderRadius="full"
            border="1px solid"
            borderColor="gray.400"
            fontSize="xs"
            cursor="help"
            aria-label="Collection info"
            tabIndex={0}
          >
            ?
          </Box>
        </Tooltip>
      </HStack>
      <RadioCard.Root
        value={collectionReplacement ? "all" : "specific"}
        onValueChange={(details) => {
          const collectAll = details.value === "all"
          onCollectionReplacementChange(collectAll)
          if (collectAll) {
            onQuantityCollectedChange("")
          }
        }}
        orientation="horizontal"
        data-testid="collection-replacement-radio"
      >
        <HStack gap={2}>
          <RadioCard.Item value="all" flex="1" data-testid="collection-all">
            <RadioCard.ItemHiddenInput />
            <RadioCard.ItemControl>
              <RadioCard.ItemIndicator />
              <RadioCard.ItemText fontSize="sm" whiteSpace="nowrap">Collect all remaining</RadioCard.ItemText>
            </RadioCard.ItemControl>
          </RadioCard.Item>
          <RadioCard.Item value="specific" flex="1" data-testid="collection-specific">
            <RadioCard.ItemHiddenInput />
            <RadioCard.ItemControl>
              <RadioCard.ItemIndicator />
              <RadioCard.ItemText fontSize="sm" whiteSpace="nowrap">Collect specific amount</RadioCard.ItemText>
            </RadioCard.ItemControl>
          </RadioCard.Item>
        </HStack>
      </RadioCard.Root>
      {!collectionReplacement && (
        <Field
          label="Quantity to collect (kg)"
          invalid={!!quantityCollectedError}
          errorText={quantityCollectedError}
        >
          <Input
            type="number"
            min={0}
            step="1"
            placeholder="0"
            value={quantityCollected}
            onChange={(e) => onQuantityCollectedChange(e.target.value)}
            data-testid="collection-quantity-input"
          />
        </Field>
      )}
    </VStack>
  )
}

export default CollectionSection
